import { useState, useEffect, useCallback, useMemo, useImperativeHandle, RefObject } from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
//import cn from "classnames";
import Switch from '@components/new/Switch';
import { H4, Title3, SubTitle3 } from "@components/new/Typo";
// import { Button, ButtonSize, ButtonType } from "@components/new/Button";
// import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
// import ItemSearchItem from "../../components/ItemSearchItem";
import { TabsContent, TabsList } from '@components/Tabs/Tabs.tsx';
import { Table, TableColumn } from "@components/new/Table";
import DataTable from '@containers/DataTableNew';
// import { Tooltip } from '@components/new/Tooltip';
import {
  createFBPWithdrawalRequest,
  getFBPWarehouseStocks,
  TFBPWartehouseStockItem,
  TRequestParams,
} from "@api/apiClient/fbp";
import getItemsColumns from "./itemsColumns";
import getMatchingColumns from "./matchingColumns";
import { FormHandle, TRow, TLinkRow } from "./types";
// import NoItemsIcon from "@assets/icons/hugeicons/package-remove.svg?react";
import styles from './Form.module.scss';

// type TItemWithCount = TFBPWartehouseStockItem & { present?: number }
const MAX_ITEMS = 100;

const Form = ({
  formRef,
  onStatusChange,
  onSuccess,
  onError,
  sellerWarehouseId,
  warehouseId,
  marketplaceType,
  stores,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean,
    isLoading?: boolean,
  }, msg?: React.ReactNode) => void;
  onSuccess: () => void;
  onError: () => void;
  sellerWarehouseId: string;
  marketplaceType: string;
  warehouseId: string;
  stores?: string[];
  // fbpWarehouseId: string;
}) => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<TRow[]>([]);
  //const [matchings, setMatchings] = useState<TLinkRow[]>([]);
  const [activeTab, setActiveTab] = useState("items");
  const [showUnlicnkedOnly, setShowUnlicnkedOnly] = useState(false);

  const withdrawalMutation = useMutation({
    mutationFn: createFBPWithdrawalRequest,
    onSuccess: () => onSuccess(),
    onError: () => onError(),
  });

  const stocksGetter = useCallback(
    ({ filters, sorting, page }: TRequestParams) => {
      return getFBPWarehouseStocks({
        filters,
        sorting,
        page,
        warehouseId,
        marketplaceType,
      })
        .then((data) => ({
          ...data,
          items: data.items.map((item: TFBPWartehouseStockItem) => ({
            ...item,
            present: Math.min(item.present ?? 0, 99999999),
            selected: !item.matchingLinkName,
            matchingLinkName: item.matchingLinkName,
          })),
        }));
    },
    [sellerWarehouseId]
  );

  const columns: TableColumn<TRow>[] = useMemo(() => getItemsColumns(
    t,
  ), [currentLanguage]);

  const columnsMatching: TableColumn<TLinkRow>[] = useMemo(() => getMatchingColumns(
    t,
  ), [currentLanguage, warehouseId]);

  console.log(showUnlicnkedOnly)

  const handleSelectionChange = useCallback((
    selectedIds: string[],
    items: TRow[],
  ) => {
    setSelectedItems((oldSelected: TRow[]) => (
      selectedIds.reduce((acc: TRow[], s: string) => {
        const item = oldSelected.find(v => v.id === s) || items.find(v => v.id === s);
        if (item) return [...acc, item];
        return acc;
      }, [])
    ));
  }, []);

  const tabs = [
    {
      id: 'items',
      title: t('fbpPage.delSt oreModal.tabItems'),
      content: (
        <div className={styles.content}>
          {false && <Switch
            checked={showUnlicnkedOnly}
            onChange={() => setShowUnlicnkedOnly(!showUnlicnkedOnly)}
            title={t('fbpPage.delStoreModal.unLinkedItems')}
          />}

          <DataTable<TRow>
            selectable
            isCheckboxDisabled={(data: TRow) => (selectedItems.length >= MAX_ITEMS && !selectedItems.find(v => data.id === v.id))}
            checkboxTooltip={(data: TRow) => ((selectedItems.length >= MAX_ITEMS && !selectedItems.find(v => data.id === v.id)) ? t("fbpPage.delStoreModal.checkboxMaxCountTooltip") : "")}
            selectedIds={selectedItems.map(v => (v.id ?? ""))}
            columns={columns}
            defaultPageSize={100}
            filters={[]}
            queryKey={["getFBPWarehouseStocks", sellerWarehouseId]}
            onSelectionChange={handleSelectionChange}
            getData={stocksGetter}
            verticalAlignment="center"
            noLoaderOnRefresh
            pending={!sellerWarehouseId}
            hidePaginationDropdown
            maxSelected={MAX_ITEMS}
            resetOnUpdate={false}
            keepSearchParams={false}
            keepSelectionOnFiltersChange
            pageSizeOptions={[MAX_ITEMS]}
          />
        </div>
      ),
    },
    {
      id: 'matchings',
      title: t('fbpPage.delStoreModal.tabMatchings'),
      content: (
        <div className={styles.content}>
          <Title3>{t("fbpPage.delStoreModal.matchingsDescription")}</Title3>

          <Table<TLinkRow>
            columns={columnsMatching}
            data={[]}
          />
        </div>
      ),
    },
  ];

  const getIsValid = () => {
    return (selectedItems?.length > 0) &&
      (selectedItems?.length <= 100);
  }

  useEffect(() => {
    onStatusChange({
      disabled: !getIsValid(),
      isLoading: Boolean(withdrawalMutation.isLoading),
    });
  }, [withdrawalMutation.isLoading, selectedItems])

  const onSubmit = () => {
    withdrawalMutation.mutate({
      sellerWarehouseId,
      ...(marketplaceType === "Yandex" ? {
        ymProducts: (selectedItems.map(v => ({
          ymProductId: v.productId,
          count: v.present ?? 0,
        }))),
        wbProducts: [],
      } : {
        ymProducts: [],
        wbProducts: (selectedItems.map(v => ({
          wbProductId: v.productId,
          count: v.present ?? 0,
        }))),
      }),
    });
  };

  useImperativeHandle(formRef, () => ({
    submit: onSubmit,
  }));

  const isMatchingAvailable = false; // todo

  return (
    <div className={styles.form}>
      {isMatchingAvailable ? (
        <TabsList
          onChange={setActiveTab}
          tabs={tabs}
          value={activeTab}
        />
      ) : (
        <div className={styles.title}>
          <H4>{t('fbpPage.delStoreModal.itemsTitle')}</H4>
          {stores?.length ? (
            stores.length === 1 ? (
              <SubTitle3 color="grey-800">{
                t('fbpPage.delStoreModal.itemsDescriptionWithStore', { store: stores[0] })
              }</SubTitle3>
            ) : (
              <SubTitle3 color="grey-800">{
                t('fbpPage.delStoreModal.itemsDescriptionWithStores', {
                  stores: stores.slice(0, -1).join(", "),
                  lastStore: stores[stores.length - 1],
                })
              }</SubTitle3>
            )
          ) : (
            <SubTitle3 color="grey-800">{t('fbpPage.delStoreModal.itemsDescription')}</SubTitle3>
          )}
        </div>
      )}

      <TabsContent
        tab={tabs.find((tab) => (tab.id === activeTab))}
      />
    </div>
  )
};

export default Form;
